// Messaging Service for Guest-Host Communication (API-based)

import { getApiUrl } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/**
 * Send message from Guest to Host (via API - host receives on their device)
 */
export const sendGuestToHostMessage = async (messageData) => {
  try {
    const { guestId, hostId, bookingId, propertyName, message } = messageData;
    const res = await fetch(getApiUrl('/api/messages/send'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        toId: hostId,
        bookingId,
        propertyName: propertyName || '',
        message: message.trim()
      })
    });
    let data;
    try {
      data = await res.json();
    } catch (jsonError) {
      return { success: false, error: `Server returned ${res.status} ${res.statusText}` };
    }
    if (!res.ok || !data.success) {
      return {
        success: false,
        error: data?.message || `Failed to send (${res.status} ${res.statusText})`
      };
    }
    const msg = data.message;
    const messageObj = {
      id: msg.id || msg._id,
      from: 'guest',
      fromId: guestId,
      fromName: msg.fromName,
      toId: hostId,
      bookingId,
      propertyName: msg.propertyName || propertyName,
      message: msg.message,
      timestamp: msg.timestamp || msg.createdAt,
      read: false,
      status: 'sent'
    };
    window.dispatchEvent(new Event('messagesUpdated'));
    return { success: true, message: messageObj };
  } catch (error) {
    console.error('❌ Error sending message from guest to host:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send message from Host to Guest (via API - guest receives on their device)
 */
export const sendHostToGuestMessage = async (messageData) => {
  try {
    const { hostId, guestId, bookingId, propertyName, message } = messageData;
    const res = await fetch(getApiUrl('/api/messages/send'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        toId: guestId,
        bookingId,
        propertyName: propertyName || '',
        message: message.trim()
      })
    });
    let data;
    try {
      data = await res.json();
    } catch (jsonError) {
      return { success: false, error: `Server returned ${res.status} ${res.statusText}` };
    }
    if (!res.ok || !data.success) {
      return {
        success: false,
        error: data?.message || `Failed to send (${res.status} ${res.statusText})`
      };
    }
    const msg = data.message;
    const messageObj = {
      id: msg.id || msg._id,
      from: 'host',
      fromId: hostId,
      fromName: msg.fromName,
      toId: guestId,
      bookingId,
      propertyName: msg.propertyName || propertyName,
      message: msg.message,
      timestamp: msg.timestamp || msg.createdAt,
      read: false
    };
    window.dispatchEvent(new Event('messagesUpdated'));
    return { success: true, message: messageObj };
  } catch (error) {
    console.error('❌ Error sending message from host to guest:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch host messages from API (messages from guests)
 */
export const fetchHostMessages = async () => {
  try {
    const res = await fetch(getApiUrl('/api/messages/host'), {
      headers: getAuthHeaders()
    });
    const data = await res.json();
    if (!data.success) return [];
    return data.messages || [];
  } catch (error) {
    console.error('Error fetching host messages:', error);
    return [];
  }
};

/**
 * Fetch conversation for a booking (guest or host view)
 */
export const fetchConversation = async (bookingId) => {
  try {
    const res = await fetch(getApiUrl(`/api/messages/conversation/${bookingId}`), {
      headers: getAuthHeaders()
    });
    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.warn('Conversation API returned non-JSON:', res.status);
      return [];
    }
    const data = await res.json();
    if (!res.ok || !data.success) return [];
    const msgs = data.messages || [];
    return msgs.map(m => ({
      ...m,
      id: m.id || m._id,
      from: m.from,
      timestamp: m.timestamp || m.createdAt,
      status: m.status || 'sent'
    }));
  } catch (error) {
    console.error('Error fetching conversation:', error);
    return [];
  }
};

/**
 * Delete message via API
 */
export const deleteMessageApi = async (messageId) => {
  try {
    const res = await fetch(getApiUrl(`/api/messages/${messageId}`), {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    const data = await res.json();
    return data.success;
  } catch (error) {
    console.error('Error deleting message:', error);
    return false;
  }
};

/**
 * Mark message as read via API
 */
export const markMessageAsReadApi = async (messageId) => {
  try {
    const res = await fetch(getApiUrl(`/api/messages/${messageId}/read`), {
      method: 'PATCH',
      headers: getAuthHeaders()
    });
    const data = await res.json();
    return data.success;
  } catch (error) {
    console.error('Error marking message as read:', error);
    return false;
  }
};

/**
 * Get all messages for a user (guest or host) - legacy localStorage, prefer fetchHostMessages for host
 */
export const getMessages = (userId, userType = 'guest') => {
  try {
    const messagesKey = `messages_${userType}_${userId}`;
    const messages = JSON.parse(localStorage.getItem(messagesKey) || '[]');
    return messages;
  } catch (error) {
    console.error('Error getting messages:', error);
    return [];
  }
};

/**
 * Get messages for a specific booking
 */
export const getBookingMessages = (bookingId, userId, userType = 'guest') => {
  try {
    const allMessages = getMessages(userId, userType);
    return allMessages.filter(msg => msg.bookingId === bookingId);
  } catch (error) {
    console.error('Error getting booking messages:', error);
    return [];
  }
};

/**
 * Add guest's sent message to conversation (for ticking marks display)
 */
export const addToGuestConversation = (guestId, hostId, bookingId, messageObj) => {
  try {
    const key = `conversation_guest_${guestId}`;
    const conversations = JSON.parse(localStorage.getItem(key) || '{}');
    const convKey = `${hostId}_${bookingId}`;
    const messages = conversations[convKey] || [];
    messages.push({ ...messageObj, status: 'sent' });
    conversations[convKey] = messages;
    localStorage.setItem(key, JSON.stringify(conversations));
    window.dispatchEvent(new Event('storage'));
    return { success: true };
  } catch (error) {
    console.error('Error adding to guest conversation:', error);
    return { success: false };
  }
};

/**
 * Update guest sent message status (for ticking marks: delivered/read)
 */
export const updateGuestSentMessageStatus = (messageId, status) => {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('conversation_guest_'));
    for (const key of keys) {
      const conversations = JSON.parse(localStorage.getItem(key) || '{}');
      let updated = false;
      for (const convKey of Object.keys(conversations)) {
        const messages = conversations[convKey].map(msg =>
          msg.id === messageId ? { ...msg, status } : msg
        );
        if (JSON.stringify(messages) !== JSON.stringify(conversations[convKey])) {
          conversations[convKey] = messages;
          updated = true;
        }
      }
      if (updated) {
        localStorage.setItem(key, JSON.stringify(conversations));
        window.dispatchEvent(new Event('storage'));
        break;
      }
    }
    return { success: true };
  } catch (error) {
    console.error('Error updating guest message status:', error);
    return { success: false };
  }
};

/**
 * Get conversation for host - uses API (async)
 */
export const getHostConversation = async (hostId, guestId, bookingId) => {
  return fetchConversation(bookingId);
};

/**
 * Get conversation for guest - uses API (async)
 */
export const getGuestConversation = async (guestId, hostId, bookingId) => {
  return fetchConversation(bookingId);
};

/**
 * Mark message as read (uses API when available)
 */
export const markMessageAsRead = async (messageId, userId, userType = 'guest') => {
  const ok = await markMessageAsReadApi(messageId);
  if (ok) window.dispatchEvent(new Event('messagesUpdated'));
  return ok;
};

/**
 * Get unread message count
 */
export const getUnreadMessageCount = (userId, userType = 'guest') => {
  try {
    const unreadKey = `unreadMessages_${userType}_${userId}`;
    return parseInt(localStorage.getItem(unreadKey) || '0');
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

/**
 * Delete message
 */
export const deleteMessage = (messageId, userId, userType = 'guest') => {
  try {
    const messagesKey = `messages_${userType}_${userId}`;
    const messages = JSON.parse(localStorage.getItem(messagesKey) || '[]');
    
    const updatedMessages = messages.filter(msg => msg.id !== messageId);
    localStorage.setItem(messagesKey, JSON.stringify(updatedMessages));

    // Update unread count
    const unreadKey = `unreadMessages_${userType}_${userId}`;
    const unreadCount = updatedMessages.filter(msg => !msg.read).length;
    localStorage.setItem(unreadKey, unreadCount.toString());

    // Trigger storage event
    window.dispatchEvent(new Event('storage'));

    return { success: true };
  } catch (error) {
    console.error('Error deleting message:', error);
    return { success: false, error: error.message };
  }
};

export default {
  sendGuestToHostMessage,
  sendHostToGuestMessage,
  fetchHostMessages,
  fetchConversation,
  markMessageAsReadApi,
  getMessages,
  getBookingMessages,
  getHostConversation,
  getGuestConversation,
  addToGuestConversation,
  updateGuestSentMessageStatus,
  markMessageAsRead,
  getUnreadMessageCount,
  deleteMessage
};
